import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { speakNatural } from "../lib/speak";
import { toast } from "sonner";
import { Microphone, X, Waveform, ChatCircleDots, Stop } from "@phosphor-icons/react";

/**
 * VoiceAssistant — global floating mic button (bottom-right), hands-free Q&A about live vessel data.
 *  - Tap the floating mic → records via MediaRecorder
 *  - Auto-stops on 1.6s of silence (or 12s max)
 *  - Sends audio to /api/assistant/ask → Whisper + GPT-4o-mini + OpenAI TTS
 *  - Plays MP3 reply automatically; transcript shown in panel above the FAB.
 *  - Conversation history maintained server-side per-user for follow-up questions.
 */
export default function VoiceAssistant({ vesselId }) {
    const nav = useNavigate();
    const [open, setOpen] = useState(false);
    const [recording, setRecording] = useState(false);
    const [thinking, setThinking] = useState(false);
    const [history, setHistory] = useState([]);
    const [audioUrl, setAudioUrl] = useState(null);
    const [permError, setPermError] = useState(null);
    const mediaRef = useRef(null);
    const chunksRef = useRef([]);
    const streamRef = useRef(null);
    const audioCtxRef = useRef(null);
    const silenceTimerRef = useRef(null);
    const maxRecordTimerRef = useRef(null);

    useEffect(() => {
        return () => stopRecording(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const stopRecording = (noSubmit = false) => {
        try { silenceTimerRef.current && clearTimeout(silenceTimerRef.current); } catch {}
        try { maxRecordTimerRef.current && clearTimeout(maxRecordTimerRef.current); } catch {}
        try {
            if (mediaRef.current && mediaRef.current.state !== "inactive") {
                if (noSubmit) mediaRef.current.skipSubmit = true;
                mediaRef.current.stop();
            }
        } catch {}
        try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
        try { audioCtxRef.current?.close(); } catch {}
        streamRef.current = null;
        audioCtxRef.current = null;
        setRecording(false);
    };

    const startRecording = async () => {
        if (recording || thinking) return;
        setPermError(null);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            // Pick a mime type the browser supports
            const mimeCandidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg"];
            let mime = "";
            for (const m of mimeCandidates) {
                if (window.MediaRecorder && MediaRecorder.isTypeSupported(m)) { mime = m; break; }
            }
            const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
            chunksRef.current = [];
            rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
            rec.onstop = async () => {
                if (rec.skipSubmit) return;
                const blob = new Blob(chunksRef.current, { type: mime || "audio/webm" });
                if (blob.size < 1500) {
                    toast.error("Didn't catch that — try again");
                    return;
                }
                await sendQuery(blob);
            };
            rec.start(250);
            mediaRef.current = rec;
            setRecording(true);

            // Hard cap at 12s
            maxRecordTimerRef.current = setTimeout(() => stopRecording(false), 12000);

            // Silence detection via Web Audio API
            const AudioCtx = window.AudioContext || window.webkitAudioContext;
            const ctx = new AudioCtx();
            audioCtxRef.current = ctx;
            const source = ctx.createMediaStreamSource(stream);
            const analyser = ctx.createAnalyser();
            analyser.fftSize = 1024;
            source.connect(analyser);
            const buf = new Uint8Array(analyser.fftSize);
            let lastSpeech = Date.now();
            const checkSilence = () => {
                if (!streamRef.current) return;
                analyser.getByteTimeDomainData(buf);
                let max = 0;
                for (let i = 0; i < buf.length; i++) {
                    const v = Math.abs(buf[i] - 128);
                    if (v > max) max = v;
                }
                // Threshold ~6 = ambient noise floor; >12 = speech
                if (max > 12) lastSpeech = Date.now();
                if (Date.now() - lastSpeech > 1600 && (Date.now() - lastSpeech) < 4000) {
                    // ~1.6s silence after speech detected → stop
                    stopRecording(false);
                    return;
                }
                if (streamRef.current && audioCtxRef.current) requestAnimationFrame(checkSilence);
            };
            requestAnimationFrame(checkSilence);
        } catch (err) {
            setPermError(err.message || "Microphone permission denied");
            setRecording(false);
        }
    };

    const sendQuery = async (blob) => {
        setThinking(true);
        try {
            const fd = new FormData();
            fd.append("audio", blob, "voice.webm");
            if (vesselId) fd.append("vessel_id", vesselId);
            const { data } = await api.post("/assistant/ask", fd, { headers: { "Content-Type": "multipart/form-data" } });
            const entry = { question: data.question, answer: data.answer, at: Date.now() };
            setHistory((prev) => [...prev, entry]);
            if (data.audio_b64) {
                const url = `data:audio/mpeg;base64,${data.audio_b64}`;
                setAudioUrl(url);
            } else if (data.answer) {
                // Browser fallback — natural male voice if available
                speakNatural(data.answer);
            }
            // Voice-driven navigation
            if (data.nav_to && typeof data.nav_to === "string" && data.nav_to.startsWith("/")) {
                setTimeout(() => nav(data.nav_to), 600);
            }
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setThinking(false);
        }
    };

    return (
        <>
            {/* Floating mic button — bottom-right, visible on every page */}
            <button
                onClick={() => setOpen((v) => !v)}
                className={`fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full shadow-2xl flex items-center justify-center text-white transition-all hover:scale-110 ${
                    recording ? "bg-[var(--grindrod-red)] animate-pulse" :
                    thinking ? "bg-[var(--grindrod-navy)]" :
                    "bg-[var(--grindrod-navy)] hover:bg-[#0F2553]"
                }`}
                style={{ boxShadow: "0 12px 32px rgba(11,27,61,.35)" }}
                data-testid="voice-assistant-fab"
                aria-label="Voice assistant"
            >
                <Microphone size={26} weight="fill" />
            </button>

            {open && (
                <div className="fixed bottom-24 right-6 z-50 w-[min(380px,calc(100vw-32px))] g-card overflow-hidden shadow-2xl" data-testid="voice-assistant-panel">
                    <div className="bg-[var(--grindrod-navy)] text-white px-4 py-3 flex items-center gap-3 border-b-[3px] border-[var(--grindrod-red)]">
                        <ChatCircleDots size={18} weight="duotone" />
                        <div className="flex-1 min-w-0">
                            <div className="uppercase-label text-[9px] text-slate-300">Voice Assistant</div>
                            <div className="font-display font-bold text-sm">Hands-free Q&amp;A</div>
                        </div>
                        <button onClick={() => { stopRecording(true); setOpen(false); }} className="text-white/80 hover:text-white" data-testid="voice-close">
                            <X size={16} />
                        </button>
                    </div>

                    <div className="max-h-[300px] overflow-y-auto p-3 space-y-3" data-testid="voice-history">
                        {history.length === 0 && !recording && !thinking && (
                            <div className="text-xs text-slate-500 leading-relaxed">
                                Tap the big red button below and ask anything:
                                <ul className="mt-2 space-y-1 list-disc pl-5 text-slate-600">
                                    <li>What's our flow rate?</li>
                                    <li>What's the ETA on this vessel?</li>
                                    <li>When is our next vessel?</li>
                                    <li>How many tons did we shift this month?</li>
                                    <li>Read me the report.</li>
                                </ul>
                            </div>
                        )}
                        {history.map((h, i) => (
                            <div key={i} className="space-y-1.5">
                                <div className="text-[10px] uppercase-label text-slate-400">You</div>
                                <div className="text-sm text-[var(--grindrod-navy)] font-semibold bg-slate-50 p-2 rounded-sm">{h.question}</div>
                                <div className="text-[10px] uppercase-label text-[var(--grindrod-red)]">Assistant</div>
                                <div className="text-sm text-slate-700 bg-[var(--grindrod-green-bg,#e6f7eb)] p-2 rounded-sm border-l-2 border-[var(--grindrod-green)]">
                                    {h.answer}
                                </div>
                            </div>
                        ))}
                        {thinking && (
                            <div className="text-xs text-slate-500 inline-flex items-center gap-2 animate-pulse">
                                <Waveform size={14} className="text-[var(--grindrod-navy)]" /> Thinking…
                            </div>
                        )}
                        {permError && (
                            <div className="text-xs text-[var(--grindrod-red)]">{permError}</div>
                        )}
                    </div>

                    <div className="p-3 border-t border-slate-100">
                        <div className="flex items-stretch gap-2">
                            <button
                                onClick={recording ? () => stopRecording(false) : startRecording}
                                disabled={thinking}
                                className={`flex-1 py-3 rounded-sm font-display font-bold uppercase tracking-wide text-sm transition-all flex items-center justify-center gap-2 ${
                                    recording ? "bg-[var(--grindrod-red)] text-white animate-pulse" :
                                    "bg-[var(--grindrod-navy)] hover:bg-[#0F2553] text-white disabled:opacity-50"
                                }`}
                                data-testid="voice-record-btn"
                            >
                                <Microphone size={16} weight="fill" />
                                {recording ? "Listening… tap to stop" : thinking ? "Thinking…" : "Tap & speak"}
                            </button>
                            <button
                                onClick={() => {
                                    try { window.speechSynthesis && window.speechSynthesis.cancel(); } catch { /* ignore */ }
                                    setAudioUrl(null);
                                    if (recording) stopRecording(true);
                                }}
                                className="px-3 rounded-sm bg-slate-100 hover:bg-slate-200 text-[var(--grindrod-navy)] flex items-center justify-center transition-colors"
                                title="Stop speaking / cancel"
                                data-testid="voice-stop-btn"
                                aria-label="Stop voice assistant"
                            >
                                <Stop size={18} weight="fill" />
                            </button>
                        </div>
                        <div className="text-[10px] text-slate-400 text-center mt-1.5">
                            Auto-stops on silence · max 12s · Stop button cancels speech
                        </div>
                    </div>

                    {audioUrl && (
                        <audio src={audioUrl} autoPlay onEnded={() => setAudioUrl(null)} data-testid="voice-audio" />
                    )}
                </div>
            )}
        </>
    );
}
